import React, { useMemo, useState } from 'react';
import { Box, Stack, TextField } from '@mui/material';
import { useAppAuth } from 'src/context/use-app-auth';
import { Screen } from 'src/components/screen';
import { useTranslation } from 'src/text';
import { useSettings } from 'src/presentation/settings/settings-context';
import { container, contentContainer } from './styles';
import { switchPrimary } from 'src/presentation/engagement/reviews/styles';
import { LocationSelect } from 'src/components/location-select';
import { uploadSettingsLogo } from 'src/api/services/media-upload';
import { SettingsSection } from './components/section';
import { QrSection } from './components/qr-section';
import { WelcomeEmailSection } from './components/welcome-email-section';
import { LogoSection } from './components/branding-section';
import { GuidelinesSection } from './components/guidelines-section';

const SettingsScreen: React.FC = () => {
  const { location, isAdmin, date } = useAppAuth();

  const { settingsLabels, genericLabels } = useTranslation();
  const {
    guidelines,
    setGuidelines,
    autoReplyEnabled,
    setAutoReplyEnabled,
    saving,
    saveGuidelines,
    reviewLandingMessage,
    setReviewLandingMessage,
    checkinsWelcomeSubject,
    setCheckinsWelcomeSubject,
    checkinsWelcomeMessage,
    setCheckinsWelcomeMessage,
    logoUrl,
    setLogoUrl,
    emailTemplateHtml,
    setEmailTemplateHtml,
    checkinsOfferPercent,
    setCheckinsOfferPercent,
    checkinsOfferValidity,
    setCheckinsOfferValidity,
    checkinsOfferCode,
    setCheckinsOfferCode,
    checkinsOfferDetails,
    setCheckinsOfferDetails,
  } = useSettings();
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | undefined>();

  // Sample data for previewing email templates; these are not persisted or sent.
  const templatePreviewData = useMemo(() => {
    const addressParts = [
      ...(location?.storefrontAddress?.addressLines ?? []),
      location?.storefrontAddress?.locality,
      location?.storefrontAddress?.regionCode,
      location?.storefrontAddress?.postalCode,
    ]
      .map(part => (typeof part === 'string' ? part.trim() : ''))
      .filter(Boolean);
    const locationAddress =
      addressParts.length > 0
        ? addressParts.join(', ')
        : '123 Main St, Springfield';
    const previewBaseDate = date ? new Date(date) : new Date();
    const previewOfferValidityDays = checkinsOfferValidity ?? 14;
    const offerValidUntilDate = new Date(previewBaseDate);
    offerValidUntilDate.setDate(
      offerValidUntilDate.getDate() + previewOfferValidityDays,
    );

    return {
      name: 'Alex Guest',
      subject:
        checkinsWelcomeSubject ||
        settingsLabels.checkinsWelcomeSubjectPlaceholder ||
        'Thanks for checking in!',
      firstName: 'Alex',
      lastName: 'Guest',
      message:
        checkinsWelcomeMessage ||
        settingsLabels.checkinsWelcomeMessagePlaceholder ||
        'Hi there! Thanks for checking in with us. Here is an offer for your next visit.',
      email: 'guest@example.com',
      businessName: location?.title || location?.name || 'Your Business',
      locationName: location?.title || location?.name || 'Your Business',
      locationAddress,
      locationPhone: location?.phoneNumbers?.primaryPhone || '(555) 555-1234',
      websiteUri: location?.websiteUri || 'https://example.com',
      currentDate: date || new Date().toLocaleDateString('en-US'),
      offerPercent: checkinsOfferPercent ?? 20,
      offerValidity: previewOfferValidityDays,
      offerValidUntil: offerValidUntilDate.toLocaleDateString('en-GB'),
      offerCode: checkinsOfferCode || 'WELCOME20',
      offerDetails: checkinsOfferDetails || 'Present this email to redeem.',
      logoUrl:
        logoUrl || 'https://via.placeholder.com/200x80?text=Your+Logo+Here',
    };
  }, [
    location?.name,
    location?.title,
    location?.storefrontAddress?.addressLines,
    location?.storefrontAddress?.locality,
    location?.storefrontAddress?.regionCode,
    location?.storefrontAddress?.postalCode,
    location?.phoneNumbers?.primaryPhone,
    location?.websiteUri,
    date,
    logoUrl,
    checkinsOfferPercent,
    checkinsOfferValidity,
    checkinsOfferCode,
    checkinsOfferDetails,
    checkinsWelcomeMessage,
    checkinsWelcomeSubject,
    settingsLabels.checkinsWelcomeMessagePlaceholder,
    settingsLabels.checkinsWelcomeSubjectPlaceholder,
  ]);

  // Replace {{placeholder}} tokens in the admin HTML with sample preview data.
  const emailTemplatePreview = useMemo(() => {
    if (!emailTemplateHtml) return '';
    return emailTemplateHtml.replace(
      /\{\{\s*([\w.-]+)\s*\}\}/g,
      (match, key: string) => {
        const val = (templatePreviewData as Record<string, unknown>)[key];
        return val === undefined || val === null ? match : String(val);
      },
    );
  }, [emailTemplateHtml, templatePreviewData]);

  const handleSave = async () => {
    await saveGuidelines();
  };

  const qrTarget: string = useMemo(() => {
    const locId = location?.key;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    if (base && locId) {
      return `${base}/review/${encodeURIComponent(locId)}`;
    }
    return 'https://example.com/company';
  }, [location?.key]);

  const logoLabels = {
    checkinsEmailLogoLabel: settingsLabels.checkinsEmailLogoLabel,
    checkinsEmailLogoHelp: settingsLabels.checkinsEmailLogoHelp,
    logoPlaceholder: settingsLabels.logoPlaceholder,
    logoUploading: settingsLabels.logoUploading,
    uploadLogoCta: settingsLabels.uploadLogoCta,
  };

  const qrLabels = {
    qrDownload: settingsLabels.qrDownload,
    qrAlt: settingsLabels.qrAlt,
    reviewLandingLabel: settingsLabels.reviewLandingLabel,
    reviewLandingPlaceholder: settingsLabels.reviewLandingPlaceholder,
  };

  const welcomeLabels = {
    checkinsWelcomeSubjectLabel: settingsLabels.checkinsWelcomeSubjectLabel,
    checkinsWelcomeSubjectPlaceholder:
      settingsLabels.checkinsWelcomeSubjectPlaceholder,
    checkinsWelcomeMessageLabel: settingsLabels.checkinsWelcomeMessageLabel,
    checkinsWelcomeMessagePlaceholder:
      settingsLabels.checkinsWelcomeMessagePlaceholder,
    checkinsOfferPercentLabel: settingsLabels.checkinsOfferPercentLabel,
    checkinsOfferPercentPlaceholder:
      settingsLabels.checkinsOfferPercentPlaceholder,
    checkinsOfferValidityLabel: settingsLabels.checkinsOfferValidityLabel,
    checkinsOfferValidityPlaceholder:
      settingsLabels.checkinsOfferValidityPlaceholder,
    checkinsOfferCodeLabel: settingsLabels.checkinsOfferCodeLabel,
    checkinsOfferCodePlaceholder: settingsLabels.checkinsOfferCodePlaceholder,
    checkinsOfferDetailsLabel: settingsLabels.checkinsOfferDetailsLabel,
    checkinsOfferDetailsPlaceholder:
      settingsLabels.checkinsOfferDetailsPlaceholder,
  };

  const guidelineLabels = {
    placeholder: settingsLabels.placeholder,
    autoReplyLabel: settingsLabels.autoReplyLabel,
    saving: settingsLabels.saving,
  };

  const handleLogoUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    // reset input so the same file can be selected again if needed
    e.target.value = '';
    if (!file) return;
    if (!location?.key) {
      setLogoError(settingsLabels.logoLocationRequired || 'Pick a location.');
      return;
    }
    setLogoError(undefined);
    setLogoUploading(true);
    try {
      const url = await uploadSettingsLogo(file, location.key);
      setLogoUrl(url);
      // Persist the new logo immediately so it shows up across sessions and in the QR code.
      await saveGuidelines({ logoUrl: url });
    } catch (err) {
      setLogoError(
        settingsLabels.logoUploadFailed || 'Unable to upload logo right now.',
      );
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <Screen>
      <Box sx={container}>
        <Box sx={contentContainer}>
          <Stack spacing={4}>
            <LocationSelect />

            <SettingsSection
              title={settingsLabels.checkinsEmailLogoLabel || 'Logo'}
              tooltip={
                settingsLabels.checkinsEmailLogoHelp ||
                'Shown at the top of the welcome email.'
              }>
              <LogoSection
                logoUrl={logoUrl}
                logoUploading={logoUploading}
                logoError={logoError}
                onLogoUpload={handleLogoUpload}
                labels={logoLabels}
              />
            </SettingsSection>

            <SettingsSection
              title={settingsLabels.qrTitle}
              tooltip={settingsLabels.qrInfo}>
              <QrSection
                logoUrl={logoUrl}
                target={qrTarget}
                reviewLandingMessage={reviewLandingMessage}
                onReviewLandingChange={setReviewLandingMessage}
                labels={qrLabels}
              />
            </SettingsSection>

            {isAdmin ? (
              <SettingsSection
                title="Email template (admin only)"
                tooltip="HTML template used for outbound emails; supports {{placeholders}}.">
                <Stack spacing={2}>
                  <TextField
                    label="HTML template"
                    multiline
                    minRows={6}
                    maxRows={16}
                    value={emailTemplateHtml}
                    onChange={e => setEmailTemplateHtml(e.target.value)}
                    placeholder="<p>Hello {{name}},</p><p>{{message}}</p>"
                    sx={{
                      '& .MuiInputBase-root': {
                        maxHeight: 500,
                        overflow: 'auto',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: 500,
                      overflow: 'auto',
                      bgcolor: 'background.paper',
                    }}
                    aria-label="Email template preview">
                    {emailTemplateHtml ? (
                      <div
                        // Admin-authored HTML preview; kept contained within this box.
                        dangerouslySetInnerHTML={{
                          __html: emailTemplatePreview,
                        }}
                      />
                    ) : (
                      <Box sx={{ color: 'text.secondary' }}>
                        Add HTML above to see a preview here.
                      </Box>
                    )}
                  </Box>
                </Stack>
              </SettingsSection>
            ) : null}

            <SettingsSection
              title={
                settingsLabels.checkinsWelcomeTitle || 'Check-in welcome email'
              }
              tooltip={
                settingsLabels.checkinsWelcomeInfo ||
                'Default email sent to guests who opt in at check-in.'
              }>
              <WelcomeEmailSection
                subject={checkinsWelcomeSubject}
                message={checkinsWelcomeMessage}
                onSubjectChange={setCheckinsWelcomeSubject}
                onMessageChange={setCheckinsWelcomeMessage}
                labels={welcomeLabels}
                offerPercent={checkinsOfferPercent}
                onOfferPercentChange={setCheckinsOfferPercent}
                offerValidity={checkinsOfferValidity}
                onOfferValidityChange={setCheckinsOfferValidity}
                offerCode={checkinsOfferCode}
                onOfferCodeChange={setCheckinsOfferCode}
                offerDetails={checkinsOfferDetails}
                onOfferDetailsChange={setCheckinsOfferDetails}
              />
            </SettingsSection>

            <SettingsSection
              title={settingsLabels.title}
              tooltip={settingsLabels.info}>
              <GuidelinesSection
                guidelines={guidelines}
                onGuidelinesChange={setGuidelines}
                autoReplyEnabled={autoReplyEnabled}
                onAutoReplyChange={setAutoReplyEnabled}
                saving={saving}
                onSave={handleSave}
                labels={guidelineLabels}
                saveLabel={genericLabels.save}
                switchSx={switchPrimary}
              />
            </SettingsSection>
          </Stack>
        </Box>
      </Box>
    </Screen>
  );
};

export { SettingsScreen };
